import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./components/layout/AdminLayout";
import { apiService } from "./services/apiService";
import { Spinner } from "./components/ui/Spinner";
import { ErrorState } from "./components/ui/ErrorState";
import { Toast } from "./components/ui/Toast";

type PaymentFlag = {
  key: string;
  enabled: boolean;
  source?: "database" | "default" | "environment";
  envPaywallEnabled?: boolean;
  updatedBy?: string;
  updatedAt?: string;
};

type RestorePurchaseFlag = PaymentFlag & { key: "restorePurchase" };

type FeatureFlagsResponse = {
  success: boolean;
  data: {
    payment: PaymentFlag;
    restorePurchase: RestorePurchaseFlag;
  };
};

const FeatureFlags = () => {
  const navigate = useNavigate();
  const [paymentFlag, setPaymentFlag] = useState<PaymentFlag | null>(null);
  const [restorePurchaseFlag, setRestorePurchaseFlag] =
    useState<RestorePurchaseFlag | null>(null);
  const [updatedBy, setUpdatedBy] = useState(
    sessionStorage.getItem("adminUserName") || "admin",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem("adminLoggedIn");
    if (isLoggedIn !== "true") {
      navigate("/admin/login");
    }
  }, [navigate]);

  const loadFlags = async () => {
    setLoading(true);
    setError(null);
    try {
      let flag: PaymentFlag | null = null;
      try {
        const res = await apiService.get<FeatureFlagsResponse>("feature-flags");
        flag = res.data?.payment;
        setRestorePurchaseFlag(res.data?.restorePurchase);
      } catch {
        const res = await apiService.get<any>(
          "portal-api/feature-flags/payment",
        );
        flag = res.data?.payment || res.data;
        const restoreRes = await apiService.get<any>(
          "portal-api/feature-flags/restorePurchase",
        );
        setRestorePurchaseFlag(restoreRes.data?.restorePurchase || restoreRes.data);
      }
      setPaymentFlag(flag);
    } catch (err: any) {
      setError(err?.message || "Failed to load feature flags.");
    } finally {
      setLoading(false);
    }
  };

  const updateRestorePurchaseFlag = async (enabled: boolean) => {
    setSaving(true);
    try {
      const res = await apiService.patch<FeatureFlagsResponse>(
        "portal-api/feature-flags/restorePurchase",
        {
          enabled,
          updatedBy: updatedBy.trim() || "admin",
          description: enabled
            ? "Restore purchase button enabled from admin dashboard"
            : "Restore purchase button disabled from admin dashboard",
        },
      );
      setRestorePurchaseFlag(
        (res.data as any).restorePurchase || (res.data as any),
      );
      setToast({
        message: enabled
          ? "Restore purchase button enabled."
          : "Restore purchase button disabled.",
        type: "success",
      });
      await loadFlags();
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to update flag.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadFlags();
  }, []);

  const updatePaymentFlag = async (enabled: boolean) => {
    setSaving(true);
    try {
      if (updatedBy.trim()) {
        sessionStorage.setItem("adminUserName", updatedBy.trim());
      }

      const res = await apiService.patch<FeatureFlagsResponse>(
        "portal-api/feature-flags/payment",
        {
          enabled,
          updatedBy: updatedBy.trim() || "admin",
          description: enabled
            ? "Payment feature enabled from admin dashboard"
            : "Payment feature disabled from admin dashboard",
        },
      );

      setPaymentFlag(res.data.payment || (res.data as any));
      setToast({
        message: enabled
          ? "Payment feature enabled."
          : "Payment feature disabled. App access will stay open.",
        type: "success",
      });
      await loadFlags();
    } catch (err: any) {
      setToast({
        message: err?.message || "Failed to update payment flag.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      searchQuery=''
      onSearchChange={() => undefined}
      searchPlaceholder='Feature flags'>
      <div className='flex justify-between items-start mb-6'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 tracking-tight'>
            Feature Flags
          </h1>
          <p className='text-sm text-gray-500 mt-1.5 font-medium'>
            Turn release features on or off without shipping a new app build.
          </p>
        </div>
        <button
          onClick={loadFlags}
          className='px-4 py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50'>
          Refresh
        </button>
      </div>

      {loading ? (
        <Spinner text='Loading feature flags...' />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <div className='space-y-5 max-w-3xl'>
          <div className='bg-white border border-gray-200 rounded-2xl shadow-sm p-5'>
            <div className='flex items-start justify-between gap-4'>
              <div>
                <div className='flex items-center gap-2'>
                  <h2 className='text-lg font-extrabold text-gray-900'>
                    Payment Feature
                  </h2>
                  <span
                    className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      paymentFlag?.enabled
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}>
                    {paymentFlag?.enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className='text-sm text-gray-500 mt-2 leading-6'>
                  When disabled, mobile users can continue normal app flow and
                  tax report downloads without seeing the payment wall.
                </p>
                <div className='mt-3 text-xs text-gray-400 font-semibold space-y-1'>
                  <div>Source: {paymentFlag?.source || "unknown"}</div>
                  <div>
                    Env paywall:{" "}
                    {paymentFlag?.envPaywallEnabled === false ? "Off" : "On"}
                  </div>
                  {paymentFlag?.updatedAt && (
                    <div>
                      Last updated:{" "}
                      {new Date(paymentFlag.updatedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <button
                disabled={saving}
                onClick={() => updatePaymentFlag(!paymentFlag?.enabled)}
                className={`px-5 py-2.5 rounded-xl text-sm font-extrabold text-white disabled:opacity-60 ${
                  paymentFlag?.enabled
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}>
                {saving
                  ? "Saving..."
                  : paymentFlag?.enabled
                    ? "Disable Payment"
                    : "Enable Payment"}
              </button>
            </div>
          </div>

          <div className='bg-white border border-gray-200 rounded-2xl shadow-sm p-5'>
            <div className='flex items-start justify-between gap-4'>
              <div>
                <div className='flex items-center gap-2'>
                  <h2 className='text-lg font-extrabold text-gray-900'>
                    Restore Purchase Button
                  </h2>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${restorePurchaseFlag?.enabled !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {restorePurchaseFlag?.enabled !== false ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className='text-sm text-gray-500 mt-2 leading-6'>
                  Controls whether users can restore a previous store purchase.
                </p>
                <div className='mt-3 text-xs text-gray-400 font-semibold'>
                  Source: {restorePurchaseFlag?.source || "default"}
                </div>
              </div>
              <button
                disabled={saving}
                onClick={() => updateRestorePurchaseFlag(restorePurchaseFlag?.enabled === false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-extrabold text-white disabled:opacity-60 ${restorePurchaseFlag?.enabled !== false ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}>
                {saving ? "Saving..." : restorePurchaseFlag?.enabled !== false ? "Disable Restore" : "Enable Restore"}
              </button>
            </div>
          </div>

          <div className='bg-white border border-gray-200 rounded-2xl shadow-sm p-5'>
            <h2 className='text-lg font-extrabold text-gray-900'>
              Update Details
            </h2>
            <p className='text-sm text-gray-500 mt-2'>
              The name below is saved on the flag so we know who changed it.
            </p>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
              <label className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
                Updated By
                <input
                  value={updatedBy}
                  onChange={(event) => setUpdatedBy(event.target.value)}
                  className='mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E27D00]'
                />
              </label>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminLayout>
  );
};

export default FeatureFlags;
